import React, { useContext, useImperativeHandle, useMemo, useState } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BottomTabBarHeightContext } from '@react-navigation/bottom-tabs';
import { RefreshControl, ScrollView, StyleSheet, View } from "react-native";
import { useViewportLayout } from 'utils/helper-device'
import { pageContentWidths } from 'styles/page-content'
import { useNavigation } from "@react-navigation/native";
import _ from "lodash";
import ThemedTopNavigation from "components/atoms/ThemedTopNavigation";
import HelperPlatform from "utils/helper-platform";
import ThemedKeyboardAvoidingView from 'components/atoms/ThemedKeyboardAvoidingView'
import IconBackground, { IconBackgroundSizes } from 'components/atoms/IconBackground'
import { ThemedLayoutStyles } from 'styles/components/atoms/ThemedLayout'
import { useIsInManagement } from 'utils/helper-linking'
import { TESTID } from '_shared/config/testID'
import { layoutStyles } from 'styles/layout'
import { ThemedValues } from 'styles/themed'
import { AppRoutes } from 'navigations/app-routes';
import { colorList } from 'utils/colors'

export const createRefreshControl = ( { isRefreshing = false, onRefresh = () => {
} } ) => {
	return (
		{
			refreshControl: {
				enabled: true,
				refreshing: isRefreshing,
				onRefresh: onRefresh
			}
		}
	);
}


const ThemedLayout = React.forwardRef(({ title, subtitle, style = {}, contentWidth, enableScrollView = true, scrollViewProps = {}, topNavigation = false, topTabbar = null, showBackButton = false, topNavigationProps = {}, topNavigationView = null, footerBar = true, testID = null, children }, ref) => {
	
	const navigation = useNavigation();
	const dimensions = useViewportLayout()
	const insets = useSafeAreaInsets();
	const bottomTabBarHeight = useContext( BottomTabBarHeightContext ) ?? 0;
	const isInManagement = useIsInManagement()
	const resolvedContentWidth = contentWidth ?? ( isInManagement || !enableScrollView ? 'full' : 'narrow' );
	
	const [ footerHeight, setFooterHeight ] = useState( 0 )
	
	useImperativeHandle( ref, () => {
		return (
			{
				setFooterHeight( value ) {
					setFooterHeight( value )
				},
			}
		);
	});
	
	const enableFooterBar = footerBar === true && !isInManagement
	
	let paddingBottom = enableFooterBar ? footerHeight : 10;
	if( bottomTabBarHeight > 0 ) {
		paddingBottom += bottomTabBarHeight + 32;
	} else if( HelperPlatform.isNotWeb() ) {
		let insetsBottom = _.get( insets, 'bottom', 0 );
		if ( insetsBottom < 10 ) {
			insetsBottom = 15;
		}
		
		paddingBottom += insetsBottom;
	}

	const layoutStyle = {
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'flex-start',
		paddingVertical: ThemedValues.stackSpace,
		paddingHorizontal: ThemedValues.stackSpace,
		backgroundColor: 'transparent',
		overflow: 'hidden',
		...StyleSheet.flatten( style ),
		...HelperPlatform.select({
			web: {
				width: '100%',
				alignSelf: 'center',
				maxWidth: dimensions.isSmallDevice ? undefined : pageContentWidths[ resolvedContentWidth ],
			}
		})
	};
	
	const scrollViewContainerStyle = {
		...ThemedLayoutStyles.scrollViewContentContainerStyle,
		...HelperPlatform.select({
			native: {
				paddingBottom: paddingBottom,
			},
			web: {
				minHeight: '100%',
				paddingBottom: paddingBottom,
			}
		})
	}
	
	const containerFooterStyle = HelperPlatform.select({
		native: {
			paddingBottom: paddingBottom,
		},
		web: isInManagement ? {} : {
			height: dimensions.height - ( footerHeight ),
		}
	})
	
	const scrollViewStyle = {
		...ThemedLayoutStyles.scrollViewContentStyle,
		...ThemedLayoutStyles.scrollViewContentNoFooterBarStyle,
		...containerFooterStyle
	}
	
	const topNavigationComponent = useMemo( () => {
		let view = topNavigationView;
		
		if( topNavigation !== false || showBackButton !== false ) {

			let onPressNavigateBack = undefined;
			const canShowBackButton = showBackButton === true;
			
			view = (
				<ThemedTopNavigation
					title={ title }
					subtitle={ subtitle }
					tabbar={ topTabbar }
					showBackButton={ canShowBackButton }
					{ ...topNavigationProps }
				/>
			);
		}
		
		if( view !== null ) {
			return (
				<IconBackground
					iconSize={ IconBackgroundSizes.M }
					linearGradientColours={[ colorList.PAGE_LIGHT.primary, colorList.PAGE.primary ]}
					containerStyle={ topNavigationBackgroundStyles.container }>
					
					{ view }
				</IconBackground>
			)
		}
		
		return null;
	}, [ topNavigationView, topNavigation, showBackButton, topNavigationProps, topTabbar, title, subtitle ])
	
	const layoutView = useMemo( () => {
		let layout = (
			<View
				style={ layoutStyle }>
				
				{ children }
			</View>
		);
		
		if( enableScrollView ) {
			const { refreshControl } = scrollViewProps;

			return (
				<ThemedKeyboardAvoidingView
					inScrollView={ true }
					insetBottom={ false }>

					<ScrollView
						testID={ TESTID.INTERFACE.SCROLL_VIEW }
						showsVerticalScrollIndicator={ false }
						showsHorizontalScrollIndicator={ false }
						keyboardShouldPersistTaps={'handled'}
						scrollEventThrottle={ 16 }
						refreshControl={ typeof refreshControl === 'undefined' ? null : (
							<RefreshControl
								{ ...refreshControl }
							/>
						)}
						contentContainerStyle={ scrollViewContainerStyle }
						style={ scrollViewStyle }>

						{ layout }
					</ScrollView>
				</ThemedKeyboardAvoidingView>
			);
		}
		
		return (
			<ThemedKeyboardAvoidingView
				insetBottom={ false }>
				
				{ layout }
			</ThemedKeyboardAvoidingView>
		)
	}, [ children, enableScrollView, scrollViewProps, scrollViewStyle ])
	
	return (
		<View
			style={ ThemedLayoutStyles.parentStyle }>
			
			<View
				style={[ layoutStyles.flexOne ]}>
				
				<View>
					{ topNavigationComponent }
				</View>
				
				<View
					style={ layoutStyles.flexOne }>
					
					{ layoutView }
				</View>
			</View>
		</View>
	);
})

const topNavigationBackgroundStyles = {
	container: {
		borderBottomWidth: 1,
		borderBottomColor: colorList.PRIMARY.lighter,
	},
};

export default ThemedLayout;
